import React from 'react';
import { getRarityColor, getRarityClass, getRarityLabel, getRarityGlow } from '../../utils/rarityUtils';
import './ItemTooltipContent.css';

// Helper: normalize magic from object {0:{...},1:{...}} to array [{...},{...}]
const normalizeMagic = (magic) => {
  if (!magic) return [];
  if (Array.isArray(magic)) return magic;
  if (typeof magic === 'object') return Object.values(magic);
  return [];
};

/**
 * Shared item tooltip content — the "gold standard" display for any item.
 * Renders all item stats, magic, quality, sockets, enchantments, level req, etc.
 *
 * Props:
 *   item           — the item object (required)
 *   canLearnSpells — boolean, whether the character can learn spells (for tome warning)
 *   equippedItem   — optional equipped item for stat comparison deltas
 *   compact        — if true, skip lore/description for tighter display
 */
const ItemTooltipContent = ({ item, canLearnSpells, equippedItem, compact }) => {
  if (!item) return null;

  const rarityColor = getRarityColor(item);
  const rarityLabel = getRarityLabel(item);

  // Compute comparison deltas against currently equipped item
  const deltas = [];
  if (equippedItem && equippedItem.name !== item.name) {
    const compare = (newVal, oldVal, label) => {
      const nv = parseInt(newVal) || 0;
      const ov = parseInt(oldVal) || 0;
      if (nv === 0 && ov === 0) return;
      const diff = nv - ov;
      if (diff !== 0) {
        deltas.push({
          label,
          diff: diff > 0 ? `+${diff}` : `${diff}`,
          color: diff > 0 ? '#4caf50' : '#f44336',
          icon: diff > 0 ? '▲' : '▼'
        });
      }
    };
    compare(item.att, equippedItem.att, 'Attack');
    compare(item.bonus, equippedItem.bonus, 'Defense');
    compare(item.dr, equippedItem.dr, 'Dmg Reduction');
    compare(item.size, equippedItem.size, 'Capacity');
    // Compare magic totals
    const magicSum = (raw) => {
      const sums = {};
      normalizeMagic(raw).forEach(m => { sums[m.type] = (sums[m.type] || 0) + (m.mod || m.bonus || 0); });
      return sums;
    };
    const newMagic = magicSum(item.magic);
    const oldMagic = magicSum(equippedItem.magic);
    const allTypes = new Set([...Object.keys(newMagic), ...Object.keys(oldMagic)]);
    allTypes.forEach(t => {
      const diff = (newMagic[t] || 0) - (oldMagic[t] || 0);
      if (diff !== 0) {
        deltas.push({
          label: t.toUpperCase(),
          diff: diff > 0 ? `+${diff}` : `${diff}`,
          color: diff > 0 ? '#4caf50' : '#f44336',
          icon: diff > 0 ? '▲' : '▼'
        });
      }
    });
  }

  return (
    <>
      <div className={`itt-name ${getRarityClass(item)}`} style={{ color: rarityColor, textShadow: getRarityGlow(item) }}>{item.name}</div>
      <div className="itt-rarity" style={{ color: rarityColor }}>{rarityLabel}</div>
      <div className="itt-type">{(item.type || '').toUpperCase()}</div>
      {!compact && item.description && item.description !== 'Unknown item' && <div className="itt-desc">{item.description}</div>}
      {!compact && item.lore && <div className="itt-lore">{item.lore}</div>}
      <div className="itt-divider" />

      {/* Quality & Enhancement */}
      {item.quality && item.quality !== 'standard' && (
        <div className="itt-stat" style={{ color: '#d4a017' }}>Quality: {item.quality}</div>
      )}
      {item.enhancement > 0 && (
        <div className="itt-stat" style={{ color: '#4caf50' }}>Enhanced: +{item.enhancement}</div>
      )}

      {/* Equipment stats */}
      {item.damage && <div className="itt-stat">Damage: {item.damage}</div>}
      {item.att > 0 && <div className="itt-stat">Attack: +{item.att}</div>}
      {item.bonus > 0 && <div className="itt-stat">Defense: +{item.bonus}</div>}
      {item.dr > 0 && <div className="itt-stat">Dmg Reduction: {item.dr}</div>}
      {item.mtype && <div className="itt-stat">Melee Type: {item.mtype}</div>}
      {item.size > 0 && <div className="itt-stat" style={{ color: '#d4a017' }}>Capacity: +{item.size} slots</div>}

      {/* Elemental damage */}
      {item.mdamage && item.mtype && (
        <div className="itt-stat" style={{ color: '#ff9800' }}>{item.mtype} damage: {item.mdamage}</div>
      )}

      {/* Enemy bane */}
      {item.enemy && (
        <div className="itt-stat" style={{ color: '#e74c3c' }}>Bane: {item.enemy}</div>
      )}

      {/* Sockets */}
      {item.sockets && item.sockets.length > 0 && (
        <div className="itt-stat" style={{ color: '#e91e63' }}>
          Gems: {item.sockets.filter(s => s).length}/{item.sockets.length} socketed
        </div>
      )}

      {/* Enchantments */}
      {item.enchantments && item.enchantments.length > 0 && (
        <div className="itt-magic">
          {item.enchantments.map((ench, i) => (
            <div key={i} className="itt-stat" style={{ color: '#ffb74d' }}>
              📜 {ench.label || ench.name || ench}
            </div>
          ))}
        </div>
      )}

      {/* Consumable stats */}
      {item.hp > 0 && <div className="itt-stat" style={{ color: '#e74c3c' }}>Restores {item.hp} HP</div>}
      {item.mp > 0 && <div className="itt-stat" style={{ color: '#3498db' }}>Restores {item.mp} MP</div>}

      {/* Tome info */}
      {item.spell && <div className="itt-stat" style={{ color: '#f39c12' }}>Teaches: {item.spell}</div>}
      {item.type === 'tome' && item.spellPrereqs && item.spellPrereqs.length > 0 && (
        <div className="itt-seed-reqs">
          <div className="itt-seed-header">Seed Requirements:</div>
          {item.spellPrereqs.map((p, i) => (
            <div key={i} className={`itt-seed-row${p.met ? ' met' : ' unmet'}`}>
              <span className="itt-seed-icon">{p.met ? '✓' : '✗'}</span>
              <span className="itt-seed-name">{p.seed}</span>
              <span className="itt-seed-level">Lv {p.currentLevel} / {p.reqLevel}</span>
            </div>
          ))}
        </div>
      )}
      {item.type === 'tome' && canLearnSpells === false && (
        <div className="itt-stat" style={{ color: '#e74c3c', fontStyle: 'italic' }}>⚠ Your class cannot learn magic</div>
      )}

      {/* Magic bonuses */}
      {item.magic && normalizeMagic(item.magic).length > 0 && (
        <div className="itt-magic">
          {normalizeMagic(item.magic).map((m, i) => (
            <div key={i} className="itt-stat" style={{ color: '#bf7fff' }}>
              +{m.mod || m.bonus || 0} {m.type === 'ablup' ? (m.abl || '').toUpperCase() : (m.type || '').toUpperCase()}
            </div>
          ))}
        </div>
      )}

      {/* Level requirement */}
      {item.level > 1 && <div className="itt-level">Requires Lv {item.level}</div>}

      {/* Shield class restriction */}
      {item.type === 'shield' && (
        <div className="itt-stat" style={{ color: '#ff9800', fontStyle: 'italic' }}>⚠ Non-Fighter classes: -3 ATT penalty</div>
      )}

      {/* Value */}
      <div className="itt-value">{item.cost || 0} gp (sell: {Math.floor((item.cost || 0) / 4)} gp)</div>

      {(item.quantity || 1) > 1 && (
        <div className="itt-qty">Quantity: {item.quantity}</div>
      )}

      {/* Stat comparison vs equipped */}
      {deltas.length > 0 && (
        <div className="itt-compare">
          <div className="itt-divider" />
          <div className="itt-compare-header">vs {equippedItem.name}</div>
          {deltas.map((d, i) => (
            <div key={i} className="itt-delta" style={{ color: d.color }}>
              {d.icon} {d.label}: {d.diff}
            </div>
          ))}
        </div>
      )}
    </>
  );
};

export default ItemTooltipContent;
